// src/pages/AmalanBurukDashboard.jsx
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "react-toastify";
import API from "../../../services/API";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";

// ==================== API HELPERS ====================
const getTokenHeaders = () => ({
    token: localStorage.getItem("token"),
});

const fetchAmalanSummary = (params = {}) =>
    API.get("/badDeeds/summary", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanStudentSummary = (params = {}) =>
    API.get("/badDeeds/studentSummary", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanCategorySummary = (params = {}) =>
    API.get("/badDeeds/byCategory", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchAmalanStudentList = (params = {}) =>
    API.get("/badDeeds/studentList", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const fetchStudentBadDeeds = (student_uuid) =>
    API.get("/badDeeds/viewStudentAmalanBuruk", {
        headers: getTokenHeaders(),
        params: { is_portal: true, student_uuid },
    });

const downloadStudentDisciplinePDF = (student_uuid) =>
    API.get("/badDeeds/getStudentDisciplineReportPDF", {
        headers: getTokenHeaders(),
        params: { is_portal: true, student_uuid },
        responseType: "blob",
    });

const fetchAmalanFullSummary = (params = {}) =>
    API.get("/badDeeds/monthlySummary", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

// ==================== COLORS ====================
const COLORS = [
    "#DC2626",
    "#F97316",
    "#F59E0B",
    "#7C3AED",
    "#0EA5E9",
    "#14B8A6",
    "#EC4899",
    "#64748B",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
    "#84CC16",
];

// ==================== UTILS ====================
const safeArray = (value) => (Array.isArray(value) ? value : []);

const formatNumber = (value) => Number(value || 0).toLocaleString("en-MY");

const formatDate = (date) => {
    if (!date) return "-";

    try {
        return new Date(date).toLocaleDateString("ms-MY", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "-";
    }
};

const formatTime = (value) => {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleTimeString("ms-MY", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return "-";
    }
};

const getInitial = (name) => {
    if (!name) return "?";
    return String(name).trim().charAt(0).toUpperCase();
};

const getStudentTotal = (student) =>
    Number(
        student?.total_bad_deeds ??
        student?.total ??
        student?.count ??
        student?.total_deeds ??
        0
    );

const includesText = (value, query) =>
    String(value || "").toLowerCase().includes(String(query || "").toLowerCase());

// ==================== SMALL COMPONENTS ====================
const PageShell = ({ children }) => (
    <div
        className="container-fluid"
        style={{
            minHeight: "100vh",
            background:
                "radial-gradient(circle at top left, rgba(220,38,38,0.10), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
            padding: "1.25rem",
        }}
    >
        {children}
    </div>
);

const SectionCard = ({ title, subtitle, action, children, style }) => (
    <div
        className="card border-0 mb-4"
        style={{
            borderRadius: 24,
            overflow: "hidden",
            boxShadow: "0 16px 45px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(226, 232, 240, 0.9)",
            ...style,
        }}
    >
        {(title || subtitle || action) && (
            <div
                className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3"
                style={{
                    padding: "1.15rem 1.25rem",
                    borderBottom: "1px solid #e2e8f0",
                    background: "rgba(255,255,255,0.9)",
                }}
            >
                <div>
                    {title && (
                        <h5
                            className="mb-1"
                            style={{
                                fontWeight: 900,
                                color: "#0f172a",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {title}
                        </h5>
                    )}
                    {subtitle && (
                        <div
                            style={{
                                color: "#64748b",
                                fontSize: "0.88rem",
                                fontWeight: 600,
                            }}
                        >
                            {subtitle}
                        </div>
                    )}
                </div>

                {action}
            </div>
        )}

        <div style={{ padding: "1.25rem", background: "#fff" }}>{children}</div>
    </div>
);

const LoadingState = () => (
    <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: 300, color: "#64748b" }}
    >
        <div
            className="spinner-border text-danger mb-3"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
        />
        <div style={{ fontWeight: 800, color: "#334155" }}>
            Loading discipline analytics...
        </div>
        <small>Fetching latest Masalah Disiplin records</small>
    </div>
);

const EmptyState = ({
    title = "No data available",
    subtitle = "Try another month or adjust the filters.",
}) => (
    <div
        className="text-center"
        style={{
            padding: "3rem 1rem",
            borderRadius: 20,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
        }}
    >
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>📭</div>
        <h6 style={{ fontWeight: 900, color: "#334155" }}>{title}</h6>
        <div style={{ color: "#64748b", fontSize: "0.9rem" }}>{subtitle}</div>
    </div>
);

const KpiCard = ({ label, value, subtext, icon, tone = "red" }) => {
    const tones = {
        red: {
            bg: "linear-gradient(135deg, #fef2f2 0%, #ffffff 70%)",
            iconBg: "#fee2e2",
            iconColor: "#dc2626",
            border: "#fecaca",
        },
        orange: {
            bg: "linear-gradient(135deg, #fff7ed 0%, #ffffff 70%)",
            iconBg: "#ffedd5",
            iconColor: "#ea580c",
            border: "#fed7aa",
        },
        blue: {
            bg: "linear-gradient(135deg, #eff6ff 0%, #ffffff 70%)",
            iconBg: "#dbeafe",
            iconColor: "#2563eb",
            border: "#bfdbfe",
        },
        purple: {
            bg: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 70%)",
            iconBg: "#ede9fe",
            iconColor: "#7c3aed",
            border: "#ddd6fe",
        },
    };

    const theme = tones[tone] || tones.red;

    return (
        <div className="col-12 col-sm-6 col-xl-3">
            <div
                style={{
                    height: "100%",
                    borderRadius: 22,
                    padding: "1rem",
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
                }}
            >
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: "#64748b",
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                marginBottom: 8,
                            }}
                        >
                            {label}
                        </div>

                        <div
                            style={{
                                color: "#0f172a",
                                fontSize: "1.85rem",
                                fontWeight: 950,
                                lineHeight: 1,
                                letterSpacing: "-0.05em",
                            }}
                        >
                            {value}
                        </div>

                        {subtext && (
                            <div
                                style={{
                                    color: "#64748b",
                                    fontSize: "0.78rem",
                                    marginTop: 8,
                                    fontWeight: 600,
                                }}
                            >
                                {subtext}
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 16,
                            background: theme.iconBg,
                            color: theme.iconColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.25rem",
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 15px 35px rgba(15,23,42,0.12)",
                borderRadius: 14,
                padding: "0.8rem",
            }}
        >
            <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                {label}
            </div>
            {payload.map((item, idx) => (
                <div
                    key={idx}
                    style={{
                        color: item.color,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                    }}
                >
                    {item.name}: {formatNumber(item.value)}
                </div>
            ))}
        </div>
    );
};

const StudentAvatar = ({ name }) => (
    <div
        style={{
            width: 46,
            height: 46,
            borderRadius: 16,
            background: "linear-gradient(135deg, #dc2626, #111827)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 950,
            boxShadow: "0 12px 25px rgba(15,23,42,0.16)",
            flexShrink: 0,
        }}
    >
        {getInitial(name)}
    </div>
);

const FilterInput = ({ label, children }) => (
    <div className="col-12 col-md-6 col-xl-2">
        <label
            className="form-label mb-1"
            style={{
                color: "#64748b",
                fontSize: "0.75rem",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
            }}
        >
            {label}
        </label>
        {children}
    </div>
);

// ==================== MAIN COMPONENT ====================
const AmalanBurukDashboard = () => {
    const today = new Date();
    const currentMonth = today.toISOString().slice(0, 7);

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topStudents, setTopStudents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [monthlySummary, setMonthlySummary] = useState(null);

    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentDeeds, setStudentDeeds] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [studentDetailLoading, setStudentDetailLoading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const [filters, setFilters] = useState({
        name: "",
        class: "",
        reason: "",
        teacher: "",
        startDate: "",
        endDate: "",
    });

    const filteredCategories = useMemo(() => {
        return safeArray(categories).filter(
            (c) => String(c?.category || "").toLowerCase() !== "attendance"
        );
    }, [categories]);

    const selectedCategoryData = useMemo(() => {
        if (!selectedCategory) return null;
        return filteredCategories.find((item) => item.category === selectedCategory) || null;
    }, [filteredCategories, selectedCategory]);

    const categoryBarData = useMemo(() => {
        return filteredCategories
            .slice()
            .sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
            .slice(0, 8);
    }, [filteredCategories]);

    const breakdownRows = useMemo(() => {
        return safeArray(monthlySummary?.student_breakdown);
    }, [monthlySummary]);

    const classOptions = useMemo(() => {
        return Array.from(new Set(breakdownRows.map((i) => i.class_name))).filter(Boolean);
    }, [breakdownRows]);

    const teacherOptions = useMemo(() => {
        return Array.from(new Set(breakdownRows.map((i) => i.teacher_name))).filter(Boolean);
    }, [breakdownRows]);

    const filteredBreakdownRows = useMemo(() => {
        return breakdownRows
            .filter((item) => {
                const itemDate = item.date ? new Date(item.date) : null;
                const start = filters.startDate ? new Date(filters.startDate) : null;
                const end = filters.endDate ? new Date(filters.endDate) : null;

                return (
                    includesText(item.student_name, filters.name) &&
                    includesText(item.class_name, filters.class) &&
                    includesText(item.deed_reason, filters.reason) &&
                    includesText(item.teacher_name, filters.teacher) &&
                    (!start || (itemDate && itemDate >= start)) &&
                    (!end || (itemDate && itemDate <= end))
                );
            })
            .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    }, [breakdownRows, filters]);

    const kpis = useMemo(() => {
        if (!overview) return [];

        return [
            {
                label: "Total Cases",
                value: formatNumber(overview.total_ops),
                subtext: "Discipline records this month",
                icon: "⚠️",
                tone: "red",
            },
            {
                label: "Students",
                value: formatNumber(overview.unique_students),
                subtext: "Unique students involved",
                icon: "🎓",
                tone: "blue",
            },
            {
                label: "Teachers",
                value: formatNumber(overview.unique_teachers),
                subtext: "Teachers reporting cases",
                icon: "🧑‍🏫",
                tone: "purple",
            },
            {
                label: "Categories",
                value: formatNumber(filteredCategories.length),
                subtext: "Active offence categories",
                icon: "🏷️",
                tone: "orange",
            },
        ];
    }, [overview, filteredCategories.length]);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setLoading(true);

            try {
                const [
                    resSummary,
                    resStudents,
                    resCategories,
                    resStudentList,
                    resSummaryFull,
                ] = await Promise.allSettled([
                    fetchAmalanSummary({ month: selectedMonth }),
                    fetchAmalanStudentSummary({
                        month: selectedMonth,
                        limit_top: 10,
                    }),
                    fetchAmalanCategorySummary({ month: selectedMonth }),
                    fetchAmalanStudentList({ month: selectedMonth }),
                    fetchAmalanFullSummary({ month: selectedMonth }),
                ]);

                if (!isMounted) return;

                if (
                    resSummary.status === "fulfilled" &&
                    resSummary.value?.data?.response_code === 1000
                ) {
                    setOverview(resSummary.value.data.data?.overview || null);
                    setTrend(resSummary.value.data.data?.trend || []);
                } else {
                    setOverview(null);
                    setTrend([]);
                    toast.error(resSummary.value?.data?.message || "Failed to load summary");
                }

                if (
                    resStudents.status === "fulfilled" &&
                    resStudents.value?.data?.response_code === 1000
                ) {
                    setTopStudents(resStudents.value.data.data?.leaderboard || []);
                } else {
                    setTopStudents([]);
                }

                if (
                    resCategories.status === "fulfilled" &&
                    resCategories.value?.data?.response_code === 1000
                ) {
                    setCategories(resCategories.value.data.data || []);
                } else {
                    setCategories([]);
                }

                if (
                    resStudentList.status === "fulfilled" &&
                    resStudentList.value?.data?.response_code === 1000
                ) {
                    setStudentList(resStudentList.value.data.data || []);
                } else {
                    setStudentList([]);
                }

                if (
                    resSummaryFull.status === "fulfilled" &&
                    resSummaryFull.value?.data?.response_code === 1000
                ) {
                    setMonthlySummary(resSummaryFull.value.data.data || null);
                } else {
                    setMonthlySummary(null);
                }
            } catch (err) {
                if (!isMounted) return;

                console.error("❌ Failed to fetch Amalan Buruk analytics:", err);
                toast.error(err?.message || "Failed to fetch Masalah Disiplin analytics");

                setOverview(null);
                setTrend([]);
                setTopStudents([]);
                setCategories([]);
                setStudentList([]);
                setMonthlySummary(null);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [selectedMonth]);

    const handleStudentClick = async (student) => {
        setSelectedStudent(student);
        setShowModal(true);
        setStudentDeeds([]);
        setStudentDetailLoading(true);

        try {
            const res = await fetchStudentBadDeeds(student.student_uuid);

            if (res.data.response_code === 1000) {
                setStudentDeeds(res.data.data || []);
            } else {
                toast.error(res.data.message || "Failed to load details");
            }
        } catch (err) {
            toast.error(err?.message || "Error fetching student details");
        } finally {
            setStudentDetailLoading(false);
        }
    };

    const clearFilters = () => {
        setFilters({
            name: "",
            class: "",
            reason: "",
            teacher: "",
            startDate: "",
            endDate: "",
        });
    };

    const handleDownloadPDF = async () => {
        if (!selectedStudent?.student_uuid) return;

        setDownloadingPdf(true);

        try {
            const res = await downloadStudentDisciplinePDF(selectedStudent.student_uuid);

            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `Discipline_Report_${String(selectedStudent.student_name || "student").replace(
                /\s+/g,
                "_"
            )}.pdf`;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);

            toast.success("PDF berjaya dimuat turun");
        } catch (err) {
            toast.error("Gagal memuat turun PDF");
        } finally {
            setDownloadingPdf(false);
        }
    };

    return (
        <Fragment>
            <PageShell>
                {/* Header */}
                <div
                    className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4"
                    style={{
                        background:
                            "linear-gradient(135deg, #7f1d1d 0%, #dc2626 50%, #f97316 100%)",
                        borderRadius: 28,
                        padding: "1.5rem",
                        color: "#fff",
                        boxShadow: "0 24px 60px rgba(220, 38, 38, 0.25)",
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                background: "rgba(255,255,255,0.16)",
                                border: "1px solid rgba(255,255,255,0.26)",
                                borderRadius: 999,
                                padding: "6px 12px",
                                fontSize: "0.8rem",
                                fontWeight: 900,
                                marginBottom: 12,
                            }}
                        >
                            ⚠️ Discipline Analytics
                        </div>

                        <h2
                            className="mb-1"
                            style={{
                                fontWeight: 950,
                                letterSpacing: "-0.04em",
                                fontSize: "2rem",
                            }}
                        >
                            Masalah Disiplin Dashboard
                        </h2>

                        <div
                            style={{
                                color: "rgba(255,255,255,0.82)",
                                fontWeight: 600,
                                maxWidth: 780,
                            }}
                        >
                            Monitor discipline cases, offence categories, student records,
                            teacher reporting, and monthly behaviour trends.
                        </div>
                    </div>

                    <div
                        style={{
                            background: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            borderRadius: 18,
                            padding: "0.85rem",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "rgba(255,255,255,0.75)",
                                marginBottom: 5,
                            }}
                        >
                            Reporting Month
                        </div>

                        <input
                            type="month"
                            className="form-control"
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                setSelectedCategory(null);
                                clearFilters();
                            }}
                            style={{
                                borderRadius: 14,
                                border: "none",
                                minWidth: 180,
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        />
                    </div>
                </div>

                {loading && !overview ? (
                    <SectionCard>
                        <LoadingState />
                    </SectionCard>
                ) : (
                    <>
                        {/* KPI Cards */}
                        {kpis.length > 0 && (
                            <div className="row g-3 mb-4">
                                {kpis.map((item) => (
                                    <KpiCard key={item.label} {...item} />
                                ))}
                            </div>
                        )}

                        {/* Charts */}
                        <div className="row">
                            <div className="col-12 col-xl-8">
                                <SectionCard
                                    title={`Monthly Trend (${selectedMonth})`}
                                    subtitle="Daily discipline records for the selected month"
                                >
                                    {trend.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <LineChart
                                                data={trend}
                                                margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    tickMargin={10}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    allowDecimals={false}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="total"
                                                    stroke="#DC2626"
                                                    strokeWidth={3}
                                                    dot={{ r: 3 }}
                                                    activeDot={{ r: 6 }}
                                                    name="Total Cases"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No trend data"
                                            subtitle="No daily discipline trend was found for this month."
                                        />
                                    )}
                                </SectionCard>
                            </div>

                            <div className="col-12 col-xl-4">
                                <SectionCard
                                    title="Offence Category Mix"
                                    subtitle={
                                        selectedCategoryData
                                            ? `${selectedCategoryData.category}: ${formatNumber(
                                                selectedCategoryData.total
                                            )} cases`
                                            : "Click a slice to highlight a category"
                                    }
                                    action={
                                        selectedCategory ? (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setSelectedCategory(null)}
                                                style={{ borderRadius: 999, fontWeight: 800 }}
                                            >
                                                Clear
                                            </button>
                                        ) : null
                                    }
                                >
                                    {filteredCategories.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <PieChart>
                                                <Pie
                                                    data={filteredCategories}
                                                    dataKey="total"
                                                    nameKey="category"
                                                    cx="50%"
                                                    cy="48%"
                                                    innerRadius={62}
                                                    outerRadius={112}
                                                    paddingAngle={3}
                                                    labelLine={false}
                                                    label={({ percent }) =>
                                                        `${(percent * 100).toFixed(0)}%`
                                                    }
                                                    onClick={(data) => {
                                                        if (selectedCategory === data.category) {
                                                            setSelectedCategory(null);
                                                        } else {
                                                            setSelectedCategory(data.category);
                                                            toast.info(`Filtered by category: ${data.category}`);
                                                        }
                                                    }}
                                                >
                                                    {filteredCategories.map((entry, idx) => (
                                                        <Cell
                                                            key={entry.category || idx}
                                                            fill={COLORS[idx % COLORS.length]}
                                                            style={{
                                                                cursor: "pointer",
                                                                opacity:
                                                                    selectedCategory &&
                                                                        selectedCategory !== entry.category
                                                                        ? 0.35
                                                                        : 1,
                                                            }}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend verticalAlign="bottom" align="center" layout="horizontal" />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No category data"
                                            subtitle="No offence category breakdown was found for this month."
                                        />
                                    )}
                                </SectionCard>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-xl-7">
                                <SectionCard
                                    title="Top Offence Categories"
                                    subtitle="Highest discipline categories excluding Attendance"
                                >
                                    {categoryBarData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={360}>
                                            <BarChart
                                                data={categoryBarData}
                                                layout="vertical"
                                                margin={{ top: 5, right: 20, left: 30, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    type="number"
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    allowDecimals={false}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="category"
                                                    width={150}
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Bar
                                                    dataKey="total"
                                                    name="Total Cases"
                                                    radius={[0, 10, 10, 0]}
                                                    fill="#DC2626"
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <EmptyState
                                            title="No category ranking"
                                            subtitle="Category ranking will appear after records are available."
                                        />
                                    )}
                                </SectionCard>
                            </div>

                            <div className="col-12 col-xl-5">
                                <SectionCard
                                    title="Top Students"
                                    subtitle="Students with highest recorded discipline cases"
                                >
                                    {safeArray(topStudents).length > 0 ? (
                                        <div className="d-flex flex-column gap-2">
                                            {safeArray(topStudents).slice(0, 10).map((student, idx) => (
                                                <button
                                                    key={student.student_uuid || idx}
                                                    type="button"
                                                    className="w-100 text-start"
                                                    onClick={() => handleStudentClick(student)}
                                                    style={{
                                                        border: "1px solid #e2e8f0",
                                                        background: "#f8fafc",
                                                        borderRadius: 18,
                                                        padding: "0.85rem 1rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 12,
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                                        <div
                                                            style={{
                                                                width: 38,
                                                                height: 38,
                                                                borderRadius: 14,
                                                                background: idx < 3 ? "#DC2626" : "#0f172a",
                                                                color: "#fff",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontWeight: 950,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            #{idx + 1}
                                                        </div>

                                                        <div style={{ minWidth: 0 }}>
                                                            <div
                                                                style={{
                                                                    fontWeight: 900,
                                                                    color: "#0f172a",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {student.student_name || "-"}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    color: "#64748b",
                                                                    fontSize: "0.8rem",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {student.class_name || "-"}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <span
                                                        style={{
                                                            background: "#fee2e2",
                                                            color: "#991b1b",
                                                            borderRadius: 999,
                                                            padding: "6px 12px",
                                                            fontWeight: 950,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {formatNumber(getStudentTotal(student))}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <EmptyState
                                            title="No top students"
                                            subtitle="No student ranking was found for this month."
                                        />
                                    )}
                                </SectionCard>
                            </div>
                        </div>

                        {/* Student Breakdown */}
                        <SectionCard
                            title="Perincian Kes Mengikut Murid"
                            subtitle={`${formatNumber(filteredBreakdownRows.length)} matching records from ${formatNumber(
                                breakdownRows.length
                            )} total records`}
                            action={
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={clearFilters}
                                    style={{ borderRadius: 999, fontWeight: 800 }}
                                >
                                    Clear Filters
                                </button>
                            }
                        >
                            <div
                                style={{
                                    background: "#f8fafc",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 20,
                                    padding: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <div className="row g-3">
                                    <FilterInput label="Student">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Cari nama murid..."
                                            value={filters.name}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, name: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        />
                                    </FilterInput>

                                    <FilterInput label="Class">
                                        <select
                                            className="form-control"
                                            value={filters.class}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, class: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        >
                                            <option value="">All classes</option>
                                            {classOptions.map((cls, idx) => (
                                                <option key={idx} value={cls}>
                                                    {cls}
                                                </option>
                                            ))}
                                        </select>
                                    </FilterInput>

                                    <FilterInput label="Teacher">
                                        <select
                                            className="form-control"
                                            value={filters.teacher}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, teacher: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        >
                                            <option value="">All teachers</option>
                                            {teacherOptions.map((teacher, idx) => (
                                                <option key={idx} value={teacher}>
                                                    {teacher}
                                                </option>
                                            ))}
                                        </select>
                                    </FilterInput>

                                    <FilterInput label="Reason">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Cari kesalahan..."
                                            value={filters.reason}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, reason: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        />
                                    </FilterInput>

                                    <FilterInput label="Start Date">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filters.startDate}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, startDate: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        />
                                    </FilterInput>

                                    <FilterInput label="End Date">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={filters.endDate}
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, endDate: e.target.value }))
                                            }
                                            style={{ borderRadius: 14 }}
                                        />
                                    </FilterInput>
                                </div>
                            </div>

                            {filteredBreakdownRows.length > 0 ? (
                                <div
                                    className="table-responsive"
                                    style={{
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 18,
                                        overflow: "hidden",
                                    }}
                                >
                                    <table className="table table-hover align-middle mb-0">
                                        <thead style={{ background: "#f8fafc" }}>
                                            <tr>
                                                <th>#</th>
                                                <th>Tarikh</th>
                                                <th>Masa</th>
                                                <th>Nama Murid</th>
                                                <th>Kelas</th>
                                                <th>Kesalahan</th>
                                                <th>Catatan</th>
                                                <th>Guru</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredBreakdownRows.map((item, i) => (
                                                <tr key={`${item.student_uuid || item.student_name}-${item.date}-${i}`}>
                                                    <td style={{ fontWeight: 900, color: "#94a3b8" }}>
                                                        {i + 1}
                                                    </td>
                                                    <td>{formatDate(item.date)}</td>
                                                    <td>{formatTime(item.deed_time_myt)}</td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 text-decoration-none"
                                                            onClick={() => handleStudentClick(item)}
                                                            style={{
                                                                color: "#0f172a",
                                                                fontWeight: 900,
                                                            }}
                                                        >
                                                            {item.student_name || "-"}
                                                        </button>
                                                    </td>
                                                    <td>{item.class_name || "-"}</td>
                                                    <td>
                                                        <span
                                                            style={{
                                                                background: "#fee2e2",
                                                                color: "#991b1b",
                                                                borderRadius: 999,
                                                                padding: "5px 10px",
                                                                fontWeight: 800,
                                                                display: "inline-block",
                                                            }}
                                                        >
                                                            {item.deed_reason || "-"}
                                                        </span>
                                                    </td>
                                                    <td style={{ color: "#64748b" }}>{item.remarks || "-"}</td>
                                                    <td>{item.teacher_name || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <EmptyState
                                    title="No matching records"
                                    subtitle="Try clearing filters or selecting another month."
                                />
                            )}
                        </SectionCard>

                        {/* Student List */}
                        <SectionCard
                            title="Senarai Murid"
                            subtitle={`${formatNumber(studentList.length)} students with discipline history`}
                            action={
                                <div
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        color: "#991b1b",
                                        background: "#fee2e2",
                                        border: "1px solid #fecaca",
                                        borderRadius: 999,
                                        padding: "6px 12px",
                                        fontSize: "0.78rem",
                                        fontWeight: 900,
                                    }}
                                >
                                    🔒 Admin Portal Only
                                </div>
                            }
                        >
                            {studentList.length > 0 ? (
                                <>
                                    <div
                                        style={{
                                            background: "#fff7ed",
                                            border: "1px solid #fed7aa",
                                            color: "#9a3412",
                                            borderRadius: 18,
                                            padding: "0.9rem 1rem",
                                            marginBottom: "1rem",
                                            fontSize: "0.88rem",
                                            fontWeight: 700,
                                        }}
                                    >
                                        <strong>Perhatian:</strong> Data pelajar dan rekod disiplin adalah sulit
                                        dan untuk tujuan pentadbiran sekolah sahaja.
                                    </div>

                                    <div
                                        className="table-responsive"
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 18,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <table className="table table-hover align-middle mb-0">
                                            <thead style={{ background: "#f8fafc" }}>
                                                <tr>
                                                    <th>Nama Murid</th>
                                                    <th>Kelas</th>
                                                    <th>Jumlah Kes</th>
                                                    <th>Kemaskini Terakhir</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {studentList.map((student, i) => (
                                                    <tr key={student.student_uuid || i}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <StudentAvatar name={student.student_name} />
                                                                <div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 text-decoration-none"
                                                                        onClick={() => handleStudentClick(student)}
                                                                        style={{
                                                                            color: "#0f172a",
                                                                            fontWeight: 900,
                                                                        }}
                                                                    >
                                                                        {student.student_name || "-"}
                                                                    </button>
                                                                    <div
                                                                        style={{
                                                                            color: "#64748b",
                                                                            fontSize: "0.8rem",
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {student.student_uuid || "-"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td style={{ fontWeight: 700, color: "#334155" }}>
                                                            {student.class_name || "-"}
                                                        </td>

                                                        <td>
                                                            <span
                                                                style={{
                                                                    background: "#fee2e2",
                                                                    color: "#991b1b",
                                                                    borderRadius: 999,
                                                                    padding: "7px 13px",
                                                                    fontWeight: 950,
                                                                }}
                                                            >
                                                                {formatNumber(student.total_bad_deeds || 0)}
                                                            </span>
                                                        </td>

                                                        <td>{formatDate(student.last_recorded_date)}</td>

                                                        <td className="text-end">
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                style={{ borderRadius: 999, fontWeight: 800 }}
                                                                onClick={() => handleStudentClick(student)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <EmptyState
                                    title="No student list"
                                    subtitle="No students with discipline records were found."
                                />
                            )}
                        </SectionCard>
                    </>
                )}
            </PageShell>

            {/* Modal */}
            {showModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ background: "rgba(15,23,42,0.55)" }}
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
                        <div
                            className="modal-content"
                            style={{
                                borderRadius: 24,
                                border: "none",
                                overflow: "hidden",
                                boxShadow: "0 30px 80px rgba(15,23,42,0.35)",
                            }}
                        >
                            <div
                                className="modal-header"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7f1d1d 0%, #dc2626 60%, #f97316 100%)",
                                    color: "#fff",
                                    borderBottom: "none",
                                    padding: "1.25rem",
                                }}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <StudentAvatar name={selectedStudent?.student_name} />
                                    <div>
                                        <h5 className="modal-title" style={{ fontWeight: 950 }}>
                                            Rekod Masalah Disiplin
                                        </h5>
                                        <div style={{ color: "rgba(255,255,255,0.82)", fontWeight: 600 }}>
                                            {selectedStudent?.student_name || "-"} ·{" "}
                                            {selectedStudent?.class_name || "-"}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowModal(false)}
                                />
                            </div>

                            <div className="modal-body" style={{ padding: "1.25rem" }}>
                                {studentDetailLoading ? (
                                    <LoadingState />
                                ) : studentDeeds.length === 0 ? (
                                    <EmptyState title="Tiada rekod ditemui" subtitle="No discipline record found." />
                                ) : (
                                    <div
                                        className="table-responsive"
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 18,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <table className="table table-hover align-middle mb-0">
                                            <thead style={{ background: "#f8fafc" }}>
                                                <tr>
                                                    <th>Tarikh</th>
                                                    <th>Sebab</th>
                                                    <th>Catatan</th>
                                                    <th>Status</th>
                                                    <th>Guru</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {studentDeeds.map((deed, i) => (
                                                    <tr key={i}>
                                                        <td>{formatDate(deed.date)}</td>
                                                        <td>
                                                            <span
                                                                style={{
                                                                    background: "#fee2e2",
                                                                    color: "#991b1b",
                                                                    borderRadius: 999,
                                                                    padding: "5px 10px",
                                                                    fontWeight: 800,
                                                                    display: "inline-block",
                                                                }}
                                                            >
                                                                {deed.deed_reason || "-"}
                                                            </span>
                                                        </td>
                                                        <td style={{ color: "#64748b" }}>
                                                            {deed.remarks || "-"}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`badge ${
                                                                    deed.status === "pending"
                                                                        ? "bg-warning text-dark"
                                                                        : "bg-success"
                                                                }`}
                                                                style={{
                                                                    borderRadius: 999,
                                                                    padding: "7px 11px",
                                                                }}
                                                            >
                                                                {deed.status || "-"}
                                                            </span>
                                                        </td>
                                                        <td>{deed.teacher_name || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div
                                className="modal-footer d-flex justify-content-between"
                                style={{
                                    borderTop: "1px solid #e2e8f0",
                                    padding: "1rem 1.25rem",
                                }}
                            >
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={handleDownloadPDF}
                                    disabled={downloadingPdf || !selectedStudent?.student_uuid}
                                    style={{ borderRadius: 999, fontWeight: 800 }}
                                >
                                    {downloadingPdf ? "Downloading..." : "Muat Turun PDF"}
                                </button>

                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowModal(false)}
                                    style={{ borderRadius: 999, fontWeight: 800 }}
                                >
                                    Tutup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default AmalanBurukDashboard;